import { AthleteVisualizationSelect } from "@/components/athlete/AthleteVisualizationSelect";
import type { AthleteEventRadarData } from "@/components/athlete/types";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
} from "recharts";

const radarChartConfig = {
  athlete: {
    label: "You",
    color: "#22c55e",
  },
  eventAverage: {
    label: "Event Average",
    color: "#f59e0b",
  },
  ageGroupAverage: {
    label: "Age Group Average",
    color: "#ec4899",
  },
} satisfies ChartConfig;

type RadarPoint = {
  axisLabel: string;
  segmentType: string;
  athlete: number | null;
  eventAverage: number | null;
  ageGroupAverage: number | null;
};

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

function formatResultOptionLabel(
  result: AthleteEventRadarData["context"]["availableResults"][number],
) {
  const dateText = result.eventDate
    ? dateFormatter.format(new Date(`${result.eventDate}T00:00:00Z`))
    : "Unknown date";
  return `${dateText} · ${result.eventName}`;
}

export function AthleteEventRadarPanel({
  radar,
  isLoading,
  error,
  selectedResultId,
  onResultChange,
}: {
  radar: AthleteEventRadarData | undefined;
  isLoading: boolean;
  error: unknown;
  selectedResultId: string | undefined;
  onResultChange: (resultId: string) => void;
}) {
  if (isLoading) {
    return (
      <div className="athlete-trend-empty">
        Loading event profile comparison...
      </div>
    );
  }

  if (error) {
    return (
      <div className="athlete-trend-empty">
        Event profile comparison is unavailable right now.
      </div>
    );
  }

  if (!radar || radar.context.availableResults.length === 0) {
    return (
      <div className="athlete-trend-empty">
        No race results available for split profile comparison yet.
      </div>
    );
  }

  const selectedResult = radar.context.selectedResult;
  const chartData: RadarPoint[] = radar.axes.map((axis) => ({
    axisLabel: axis.label,
    segmentType: axis.segmentType,
    athlete: radar.profiles.athlete.values[axis.segmentType] ?? null,
    eventAverage: radar.profiles.eventAverage.values[axis.segmentType] ?? null,
    ageGroupAverage:
      radar.profiles.ageGroupAverage.values[axis.segmentType] ?? null,
  }));

  return (
    <>
      <div className="athlete-trend-header">
        <div>
          <h3 className="athlete-panel-title">Race Split Profile</h3>
          <p className="athlete-split-series-meta">
            100 = event average, higher = faster
            {selectedResult.ageGroup ? ` · vs ${selectedResult.ageGroup}` : ""}
          </p>
        </div>
        <AthleteVisualizationSelect
          label="Race result"
          value={selectedResultId ?? selectedResult.resultId}
          placeholder="Select result"
          options={radar.context.availableResults.map((result) => ({
            value: result.resultId,
            label: formatResultOptionLabel(result),
          }))}
          onValueChange={onResultChange}
          triggerClassName="min-w-[240px] md:max-w-[360px]"
        />
      </div>

      {chartData.length < 3 ? (
        <div className="athlete-trend-empty">
          Not enough split data to compare this race reliably.
          <br />
          At least 3 segments with minimum sample size are required.
        </div>
      ) : (
        <ChartContainer
          config={radarChartConfig}
          className="mt-2 aspect-auto! h-[350px] min-h-[350px] md:h-[400px] md:min-h-[400px]"
        >
          <RadarChart
            data={chartData}
            margin={{ top: 8, right: 12, left: 12, bottom: 8 }}
          >
            <PolarGrid />
            <PolarAngleAxis dataKey="axisLabel" />
            <PolarRadiusAxis
              domain={[radar.meta.clampMin, radar.meta.clampMax]}
              tick={false}
              axisLine={false}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(_label, payload) =>
                    (payload?.[0]?.payload as RadarPoint | undefined)
                      ?.axisLabel ?? "Split"
                  }
                  formatter={(value, name, item) => {
                    const point = item.payload as RadarPoint;
                    const key = String(name);
                    const formatted =
                      key === "athlete"
                        ? radar.profiles.athlete.rawFormatted[point.segmentType]
                        : key === "eventAverage"
                          ? radar.profiles.eventAverage.rawFormatted[
                              point.segmentType
                            ]
                          : radar.profiles.ageGroupAverage.rawFormatted[
                              point.segmentType
                            ];
                    return `${value ?? "—"} (${formatted ?? "—"})`;
                  }}
                />
              }
            />
            <ChartLegend content={<ChartLegendContent />} />
            <Radar
              dataKey="athlete"
              stroke="var(--color-athlete)"
              fill="var(--color-athlete)"
              fillOpacity={0.2}
              strokeWidth={2.4}
              connectNulls={false}
            />
            <Radar
              dataKey="eventAverage"
              stroke="var(--color-eventAverage)"
              fill="var(--color-eventAverage)"
              fillOpacity={0.1}
              strokeWidth={2.4}
              connectNulls={false}
            />
            <Radar
              dataKey="ageGroupAverage"
              stroke="var(--color-ageGroupAverage)"
              fill="var(--color-ageGroupAverage)"
              fillOpacity={0.14}
              strokeWidth={2.4}
              connectNulls={false}
            />
          </RadarChart>
        </ChartContainer>
      )}
    </>
  );
}
